/* eslint-disable no-undef */
import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import './Voting.css';

// Contract Addresses
const GHOST_TOKEN = "0x601CE35D2aD556599F796323c98b25a8d5D17777";
const GHOST_STAKING = "0x539CF4Da5a1E0a465f877B2B827D979Bc904eD8B";
const GHOST_GOVERNANCE = "0xa004208eF6DFF8effe777B83F003ED191b4F3158";

const CHAIN_ID = 143n;
const RPC_URL = 'https://rpc.monad.xyz';

// ABIs
const ERC20_ABI = [
    "function balanceOf(address) view returns (uint256)",
    "function approve(address spender, uint256 amount) returns (bool)",
    "function allowance(address owner, address spender) view returns (uint256)"
];

const STAKING_ABI = [
    "function lock(uint256 amount, uint256 tier) external",
    "function unlock(uint256 amount) external",
    "function upgradeTier(uint256 newTier) external",
    "function claimRewards() external",
    "function delegate(address to) external",
    "function undelegate() external",
    "function depositRewards(uint256 amount) external",
    "function getAccountInfo(address account) view returns (uint256 amount, uint256 weightedAmount, uint256 lockTier, uint256 lockEndTime, uint256 multiplier, address delegatee, uint256 pending)",
    "function getRewardStats() view returns (uint256 totalDeposited, uint256 totalClaimed, uint256 totalStaked, uint256 totalWeighted, uint256 rewardPerShare)",
    "function calculatePendingRewards(address account) view returns (uint256)",
    "function owner() view returns (address)"
];

const GOVERNANCE_ABI = [
    "function propose(string title, string description) external returns (uint256)",
    "function castVote(uint256 proposalId, bool support) external",
    "function execute(uint256 proposalId) external",
    "function proposalCount() view returns (uint256)",
    "function getProposal(uint256 proposalId) view returns (address proposer, string title, string description, uint256 startTime, uint256 endTime)",
    "function getProposalVotes(uint256 proposalId) view returns (uint256 forVotes, uint256 againstVotes, bool executed, bool extended, uint8 currentState)",
    "function getReceipt(uint256 proposalId, address voter) view returns (bool hasVoted, bool support, uint256 votes)"
];

const TIER_NAMES = ["No Lock", "30 Days", "90 Days", "180 Days", "1 Year"];
const TIER_MULTIPLIERS = ["1x", "1.5x", "2x", "3x", "5x"];
const STATE_NAMES = ["Pending", "Active", "Defeated", "Timelocked", "Awaiting Execution", "Executed", "Expired"];

const GhostLogo = () => <img src="ghost.png" alt="Ghost Protocol" width="40" height="40" style={{ borderRadius: '8px' }} />;
const MonadLogo = () => <img src="monad-logo.png" alt="Monad" width="20" height="20" style={{ borderRadius: '4px' }} />;

function Voting({ onBack, onNavigate, signer: externalSigner }) {
    const [account, setAccount] = useState(null);
    const [signer, setSigner] = useState(externalSigner);
    const [ghostBalance, setGhostBalance] = useState("0");
    const [stakeInfo, setStakeInfo] = useState(null);
    const [pendingRewards, setPendingRewards] = useState("0");
    const [rewardStats, setRewardStats] = useState(null);
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    
    const [showStakeModal, setShowStakeModal] = useState(false);
    const [showUnstakeModal, setShowUnstakeModal] = useState(false);
    const [showDelegateModal, setShowDelegateModal] = useState(false);
    const [showProposalModal, setShowProposalModal] = useState(false);
    const [showVoteModal, setShowVoteModal] = useState(false);
    const [showDepositRewardsModal, setShowDepositRewardsModal] = useState(false);
    const [selectedProposal, setSelectedProposal] = useState(null);
    
    const [stakeAmount, setStakeAmount] = useState("");
    const [stakeTier, setStakeTier] = useState(0);
    const [unstakeAmount, setUnstakeAmount] = useState("");
    const [delegateAddress, setDelegateAddress] = useState("");
    const [proposalTitle, setProposalTitle] = useState("");
    const [proposalDescription, setProposalDescription] = useState("");
    const [depositRewardsAmount, setDepositRewardsAmount] = useState("");

    useEffect(() => {
        if (externalSigner) setSigner(externalSigner);
    }, [externalSigner]);

    const connectWallet = async () => {
        if (!window.ethereum) {
            setError('Please install MetaMask');
            return;
        }
        try {
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            const chainId = await window.ethereum.request({ method: 'eth_chainId' });
            
            if (BigInt(chainId) !== CHAIN_ID) {
                try {
                    await window.ethereum.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: '0x8F' }]
                    });
                } catch (switchError) {
                    if (switchError.code === 4902) {
                        await window.ethereum.request({
                            method: 'wallet_addEthereumChain',
                            params: [{
                                chainId: '0x8F',
                                chainName: 'Monad Mainnet',
                                nativeCurrency: { name: 'MON', symbol: 'MON', decimals: 18 },
                                rpcUrls: [RPC_URL],
                                blockExplorerUrls: ['https://monadvision.com']
                            }]
                        });
                    }
                }
            }
            
            const provider = new ethers.BrowserProvider(window.ethereum);
            const newSigner = await provider.getSigner();
            setSigner(newSigner);
            setAccount(accounts[0]);
            setSuccess('Wallet connected!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            setError('Failed to connect wallet');
        }
    };

    const handleNavigation = (page) => {
        if (onNavigate) onNavigate(page);
        else if (onBack) onBack();
    };

    const loadData = useCallback(async () => {
        const provider = signer || new ethers.JsonRpcProvider(RPC_URL);
        
        try {
            let address = null;
            if (signer) {
                address = await signer.getAddress();
                setAccount(address);
            }
            
            const ghostToken = new ethers.Contract(GHOST_TOKEN, ERC20_ABI, provider);
            const staking = new ethers.Contract(GHOST_STAKING, STAKING_ABI, provider);
            const governance = new ethers.Contract(GHOST_GOVERNANCE, GOVERNANCE_ABI, provider);
            
            if (signer && address) {
                const balance = await ghostToken.balanceOf(address);
                setGhostBalance(ethers.formatEther(balance));
                
                try {
                    const info = await staking.getAccountInfo(address);
                    setStakeInfo({
                        amount: ethers.formatEther(info[0]),
                        weightedAmount: ethers.formatEther(info[1]),
                        lockTier: Number(info[2]),
                        lockEndTime: Number(info[3]),
                        multiplier: Number(info[4]),
                        delegatee: info[5],
                        pending: ethers.formatEther(info[6])
                    });
                } catch (e) {
                    setStakeInfo({ amount: "0", weightedAmount: "0", lockTier: 0, lockEndTime: 0, multiplier: 100, delegatee: ethers.ZeroAddress, pending: "0" });
                }
                
                try {
                    const rewards = await staking.calculatePendingRewards(address);
                    setPendingRewards(ethers.formatEther(rewards));
                } catch (e) {
                    setPendingRewards("0");
                }
            }
            
            try {
                const stats = await staking.getRewardStats();
                setRewardStats({
                    totalDeposited: ethers.formatEther(stats[0]),
                    totalClaimed: ethers.formatEther(stats[1]),
                    totalStaked: ethers.formatEther(stats[2]),
                    totalWeighted: ethers.formatEther(stats[3])
                });
            } catch (e) {
                setRewardStats({ totalDeposited: "0", totalClaimed: "0", totalStaked: "0", totalWeighted: "0" });
            }
            
            try {
                const count = await governance.proposalCount();
                const proposalList = [];
                
                for (let i = 1; i <= Number(count); i++) {
                    try {
                        const [proposer, title, description, startTime, endTime] = await governance.getProposal(i);
                        const [forVotes, againstVotes, executed, extended, currentState] = await governance.getProposalVotes(i);
                        
                        let receipt = [false, false, 0n];
                        if (signer && address) {
                            try { receipt = await governance.getReceipt(i, address); } catch (e) {}
                        }
                        
                        proposalList.push({
                            id: i, proposer, title, description,
                            startTime: Number(startTime), endTime: Number(endTime),
                            forVotes: ethers.formatEther(forVotes), againstVotes: ethers.formatEther(againstVotes),
                            executed, extended, state: Number(currentState),
                            hasVoted: receipt[0], votedFor: receipt[1], votedAmount: ethers.formatEther(receipt[2])
                        });
                    } catch (e) {}
                }
                setProposals(proposalList.reverse());
            } catch (e) {
                setProposals([]);
            }
        } catch (err) {
            console.error("Error loading data:", err);
        }
    }, [signer]);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 30000);
        return () => clearInterval(interval);
    }, [loadData]);

    useEffect(() => {
        if (window.ethereum) {
            window.ethereum.on('accountsChanged', (accounts) => {
                if (accounts.length > 0) connectWallet();
                else { setAccount(null); setSigner(null); }
            });
            window.ethereum.on('chainChanged', () => window.location.reload());
        }
    }, []);

    const handleStake = async () => {
        if (!stakeAmount || parseFloat(stakeAmount) <= 0) { setError("Enter a valid amount"); return; }
        setLoading(true); setError("");
        try {
            const ghostToken = new ethers.Contract(GHOST_TOKEN, ERC20_ABI, signer);
            const staking = new ethers.Contract(GHOST_STAKING, STAKING_ABI, signer);
            const amount = ethers.parseEther(stakeAmount);
            const allowance = await ghostToken.allowance(account, GHOST_STAKING);
            if (allowance < amount) {
                setSuccess("Approving tokens...");
                const approveTx = await ghostToken.approve(GHOST_STAKING, amount);
                await approveTx.wait();
            }
            setSuccess("Staking tokens...");
            const tx = await staking.lock(amount, stakeTier);
            await tx.wait();
            setSuccess("Staked successfully!");
            setShowStakeModal(false); setStakeAmount(""); setStakeTier(0); loadData();
        } catch (err) { setError(err.reason || err.message || "Staking failed"); }
        setLoading(false);
    };

    const handleUnstake = async () => {
        if (!unstakeAmount || parseFloat(unstakeAmount) <= 0) { setError("Enter a valid amount"); return; }
        setLoading(true); setError("");
        try {
            const staking = new ethers.Contract(GHOST_STAKING, STAKING_ABI, signer);
            const amount = ethers.parseEther(unstakeAmount);
            setSuccess("Unstaking tokens...");
            const tx = await staking.unlock(amount);
            await tx.wait();
            setSuccess("Unstaked successfully!");
            setShowUnstakeModal(false); setUnstakeAmount(""); loadData();
        } catch (err) { setError(err.reason || err.message || "Unstaking failed"); }
        setLoading(false);
    };

    const handleClaimRewards = async () => {
        setLoading(true); setError("");
        try {
            const staking = new ethers.Contract(GHOST_STAKING, STAKING_ABI, signer);
            setSuccess("Claiming rewards...");
            const tx = await staking.claimRewards();
            await tx.wait();
            setSuccess("Rewards claimed!"); loadData();
        } catch (err) { setError(err.reason || err.message || "Claim failed"); }
        setLoading(false);
    };

    const handleDepositRewards = async () => {
        if (!depositRewardsAmount || parseFloat(depositRewardsAmount) <= 0) { setError("Enter a valid amount"); return; }
        setLoading(true); setError("");
        try {
            const ghostToken = new ethers.Contract(GHOST_TOKEN, ERC20_ABI, signer);
            const staking = new ethers.Contract(GHOST_STAKING, STAKING_ABI, signer);
            const amount = ethers.parseEther(depositRewardsAmount);
            const allowance = await ghostToken.allowance(account, GHOST_STAKING);
            if (allowance < amount) {
                setSuccess("Approving tokens...");
                const approveTx = await ghostToken.approve(GHOST_STAKING, amount);
                await approveTx.wait();
            }
            setSuccess("Depositing rewards...");
            const tx = await staking.depositRewards(amount);
            await tx.wait();
            setSuccess("Rewards deposited successfully!");
            setShowDepositRewardsModal(false); setDepositRewardsAmount(""); loadData();
        } catch (err) { setError(err.reason || err.message || "Deposit failed"); }
        setLoading(false);
    };

    const handleDelegate = async () => {
        if (!delegateAddress || !ethers.isAddress(delegateAddress)) { setError("Enter a valid address"); return; }
        setLoading(true); setError("");
        try {
            const staking = new ethers.Contract(GHOST_STAKING, STAKING_ABI, signer);
            setSuccess("Delegating...");
            const tx = await staking.delegate(delegateAddress);
            await tx.wait();
            setSuccess("Delegated successfully!");
            setShowDelegateModal(false); setDelegateAddress(""); loadData();
        } catch (err) { setError(err.reason || err.message || "Delegation failed"); }
        setLoading(false);
    };

    const handleUndelegate = async () => {
        setLoading(true); setError("");
        try {
            const staking = new ethers.Contract(GHOST_STAKING, STAKING_ABI, signer);
            setSuccess("Removing delegation...");
            const tx = await staking.undelegate();
            await tx.wait();
            setSuccess("Delegation removed!"); loadData();
        } catch (err) { setError(err.reason || err.message || "Undelegate failed"); }
        setLoading(false);
    };

    const handleCreateProposal = async () => {
        if (!proposalTitle || !proposalDescription) { setError("Enter title and description"); return; }
        setLoading(true); setError("");
        try {
            const governance = new ethers.Contract(GHOST_GOVERNANCE, GOVERNANCE_ABI, signer);
            setSuccess("Creating proposal...");
            const tx = await governance.propose(proposalTitle, proposalDescription);
            await tx.wait();
            setSuccess("Proposal created!");
            setShowProposalModal(false); setProposalTitle(""); setProposalDescription(""); loadData();
        } catch (err) { setError(err.reason || err.message || "Proposal creation failed"); }
        setLoading(false);
    };

    const handleVote = async (support) => {
        if (!selectedProposal) return;
        setLoading(true); setError("");
        try {
            const governance = new ethers.Contract(GHOST_GOVERNANCE, GOVERNANCE_ABI, signer);
            setSuccess(`Voting ${support ? 'For' : 'Against'}...`);
            const tx = await governance.castVote(selectedProposal.id, support);
            await tx.wait();
            setSuccess("Vote cast!");
            setShowVoteModal(false); setSelectedProposal(null); loadData();
        } catch (err) { setError(err.reason || err.message || "Voting failed"); }
        setLoading(false);
    };

    const handleExecute = async (proposalId) => {
        setLoading(true); setError("");
        try {
            const governance = new ethers.Contract(GHOST_GOVERNANCE, GOVERNANCE_ABI, signer);
            setSuccess("Executing proposal...");
            const tx = await governance.execute(proposalId);
            await tx.wait();
            setSuccess("Proposal executed!"); loadData();
        } catch (err) { setError(err.reason || err.message || "Execution failed"); }
        setLoading(false);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "TBD";
        return new Date(timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const canUnstake = stakeInfo && parseFloat(stakeInfo.amount) > 0 && Date.now() / 1000 > stakeInfo.lockEndTime;
    
    const getTimeUntilUnlock = () => {
        if (!stakeInfo || stakeInfo.lockEndTime === 0) return null;
        const now = Date.now() / 1000;
        if (now > stakeInfo.lockEndTime) return null;
        const diff = stakeInfo.lockEndTime - now;
        const days = Math.floor(diff / 86400);
        const hours = Math.floor((diff % 86400) / 3600);
        if (days > 0) return `${days}d ${hours}h remaining`;
        return `${hours}h remaining`;
    };

    return (
        <div className="voting-container">
            <header className="voting-header">
                <div className="voting-header-left">
                    <div className="logo" onClick={() => handleNavigation('mixer')} style={{ cursor: 'pointer' }}>
                        <GhostLogo />
                        <span className="logo-text">Ghost Protocol</span>
                    </div>
                    <nav className="voting-nav">
                        <a href="#" onClick={(e) => { e.preventDefault(); handleNavigation('mixer'); }}>Mixer</a>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleNavigation('docs'); }}>Docs</a>
                        <a href="#" className="active">Voting</a>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleNavigation('nft'); }}>NFT</a>
                    </nav>
                </div>
                <div className="voting-header-right">
                    <div className="network-badge"><MonadLogo /><span>Monad</span></div>
                    {account ? (
                        <div className="wallet-pill">{account.slice(0, 6)}...{account.slice(-4)}</div>
                    ) : (
                        <button className="connect-btn" onClick={connectWallet}>Connect Wallet</button>
                    )}
                </div>
            </header>

            {error && <div className="error-message">{error} <button onClick={() => setError("")}>×</button></div>}
            {success && <div className="success-message">{success} <button onClick={() => setSuccess("")}>×</button></div>}

            <div className="voting-stats-bar">
                <div className="voting-stat">
                    <span className="voting-stat-label">Wallet Balance</span>
                    <span className="voting-stat-value">{parseFloat(ghostBalance).toLocaleString()} GHOST</span>
                </div>
                <div className="voting-stat">
                    <span className="voting-stat-label">Staked</span>
                    <span className="voting-stat-value">{stakeInfo ? parseFloat(stakeInfo.amount).toLocaleString() : "0"} GHOST</span>
                </div>
                <div className="voting-stat">
                    <span className="voting-stat-label">Pending Rewards</span>
                    <span className="voting-stat-value">{parseFloat(pendingRewards).toLocaleString()} GHOST</span>
                </div>
                <div className="voting-stat">
                    <span className="voting-stat-label">Lock Tier</span>
                    <span className="voting-stat-value">{stakeInfo ? `${TIER_NAMES[stakeInfo.lockTier]} (${TIER_MULTIPLIERS[stakeInfo.lockTier]})` : "None"}</span>
                </div>
                <button className="manage-btn" onClick={() => setShowStakeModal(true)} disabled={!signer}>
                    <span>🔒</span> Stake
                </button>
            </div>

            <main className="voting-main">
                <div className="voting-info-section">
                    <h2>🔒 Your Staking Position</h2>
                    {!signer ? (
                        <div className="connect-prompt">
                            <p>Connect your wallet to stake GHOST and participate in governance</p>
                            <button className="connect-btn-large" onClick={connectWallet}>Connect Wallet</button>
                        </div>
                    ) : (
                        <div className="staking-grid">
                            <div className="staking-card">
                                <h3>Staked Balance</h3>
                                <div className="staking-value">{stakeInfo ? parseFloat(stakeInfo.amount).toLocaleString() : "0"} GHOST</div>
                                <div className="staking-sub">Weighted: {stakeInfo ? parseFloat(stakeInfo.weightedAmount).toLocaleString() : "0"}</div>
                                <div className="staking-buttons">
                                    <button onClick={() => setShowStakeModal(true)} disabled={loading}>Stake More</button>
                                    <button onClick={() => setShowUnstakeModal(true)} disabled={loading || !stakeInfo || parseFloat(stakeInfo.amount) === 0} className={canUnstake ? "" : "locked-btn"}>
                                        {canUnstake ? "Unstake" : (getTimeUntilUnlock() || "Unstake")}
                                    </button>
                                </div>
                            </div>
                            <div className="staking-card">
                                <h3>Pending Rewards</h3>
                                <div className="staking-value">{parseFloat(pendingRewards).toLocaleString()} GHOST</div>
                                <div className="staking-sub">Earn more with higher lock tiers</div>
                                <button onClick={handleClaimRewards} disabled={loading || parseFloat(pendingRewards) === 0}>Claim Rewards</button>
                            </div>
                            <div className="staking-card">
                                <h3>Lock Status</h3>
                                <div className="staking-value">{stakeInfo ? TIER_NAMES[stakeInfo.lockTier] : "None"}</div>
                                <div className="staking-sub">
                                    {stakeInfo && stakeInfo.lockEndTime > 0 
                                        ? (canUnstake ? "✅ Unlocked - Ready to withdraw" : `Unlocks: ${formatDate(stakeInfo.lockEndTime)}`)
                                        : "No active lock"}
                                </div>
                                <div className="staking-sub">Multiplier: {stakeInfo ? TIER_MULTIPLIERS[stakeInfo.lockTier] : "1x"}</div>
                            </div>
                            <div className="staking-card">
                                <h3>Delegation</h3>
                                <div className="staking-value">
                                    {stakeInfo && stakeInfo.delegatee !== ethers.ZeroAddress 
                                        ? `${stakeInfo.delegatee.slice(0, 6)}...${stakeInfo.delegatee.slice(-4)}`
                                        : "None"}
                                </div>
                                <div className="staking-sub">Delegate your voting power</div>
                                <div className="staking-buttons">
                                    <button onClick={() => setShowDelegateModal(true)} disabled={loading}>Delegate</button>
                                    {stakeInfo && stakeInfo.delegatee !== ethers.ZeroAddress && (
                                        <button onClick={handleUndelegate} disabled={loading}>Remove</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="voting-info-section">
                    <div className="section-header-row">
                        <h2>📊 Lock Tiers & Rewards</h2>
                        {signer && (
                            <button className="deposit-rewards-btn" onClick={() => setShowDepositRewardsModal(true)} disabled={loading}>
                                💰 Deposit Rewards
                            </button>
                        )}
                    </div>
                    <div className="tiers-table">
                        <table>
                            <thead><tr><th>Tier</th><th>Lock Duration</th><th>Reward Multiplier</th></tr></thead>
                            <tbody>
                                {TIER_NAMES.map((name, i) => (
                                    <tr key={i} className={stakeInfo?.lockTier === i ? "active-tier" : ""}>
                                        <td>{i}</td><td>{name}</td><td>{TIER_MULTIPLIERS[i]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {rewardStats && (
                        <div className="reward-stats">
                            <div className="reward-stat"><span>Total Staked:</span><strong>{parseFloat(rewardStats.totalStaked).toLocaleString()} GHOST</strong></div>
                            <div className="reward-stat"><span>Total Rewards Deposited:</span><strong>{parseFloat(rewardStats.totalDeposited).toLocaleString()} GHOST</strong></div>
                            <div className="reward-stat"><span>Total Rewards Claimed:</span><strong>{parseFloat(rewardStats.totalClaimed).toLocaleString()} GHOST</strong></div>
                        </div>
                    )}
                </div>

                <div className="voting-info-section">
                    <div className="proposals-header">
                        <h2>📋 Proposals</h2>
                        <button className="create-proposal-btn" onClick={() => setShowProposalModal(true)} disabled={!signer || loading}>+ Create Proposal</button>
                    </div>
                    {proposals.length === 0 ? (
                        <div className="proposals-empty">
                            <div className="empty-icon">📭</div>
                            <h3>No Proposals Yet</h3>
                            <p>Be the first to create a governance proposal!</p>
                            <p className="threshold-info">Requires 1,000,000 GHOST staked to create a proposal</p>
                        </div>
                    ) : (
                        <div className="proposals-list">
                            {proposals.map((proposal) => (
                                <div key={proposal.id} className="proposal-card">
                                    <div className="proposal-content">
                                        <h4>{proposal.title}</h4>
                                        <p className="proposal-description">{proposal.description}</p>
                                        <div className="proposal-meta">
                                            <span className="proposal-id">#{proposal.id}</span>
                                            <span className={`proposal-status ${STATE_NAMES[proposal.state].toLowerCase().replace(' ', '-')}`}>{STATE_NAMES[proposal.state]}</span>
                                            <span>Start: {formatDate(proposal.startTime)}</span>
                                            <span>End: {formatDate(proposal.endTime)}</span>
                                        </div>
                                    </div>
                                    <div className="proposal-votes">
                                        <div className="vote-bar">
                                            <div className="vote-bar-for" style={{ width: `${parseFloat(proposal.forVotes) + parseFloat(proposal.againstVotes) > 0 ? (parseFloat(proposal.forVotes) / (parseFloat(proposal.forVotes) + parseFloat(proposal.againstVotes))) * 100 : 50}%` }} />
                                        </div>
                                        <div className="vote-counts">
                                            <div className="vote-row for"><span>✓ For</span><span>{parseFloat(proposal.forVotes).toLocaleString()} GHOST</span></div>
                                            <div className="vote-row against"><span>✗ Against</span><span>{parseFloat(proposal.againstVotes).toLocaleString()} GHOST</span></div>
                                        </div>
                                        {proposal.hasVoted && <div className="your-vote">You voted: {proposal.votedFor ? "For" : "Against"} ({parseFloat(proposal.votedAmount).toLocaleString()} GHOST)</div>}
                                        <div className="proposal-actions">
                                            {proposal.state === 1 && !proposal.hasVoted && <button onClick={() => { setSelectedProposal(proposal); setShowVoteModal(true); }}>Vote</button>}
                                            {proposal.state === 4 && <button onClick={() => handleExecute(proposal.id)}>Execute</button>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {showStakeModal && (
                <div className="modal-overlay" onClick={() => setShowStakeModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Stake GHOST</h2>
                        <div className="modal-content">
                            <div className="input-group">
                                <label>Amount</label>
                                <input type="number" value={stakeAmount} onChange={(e) => setStakeAmount(e.target.value)} placeholder="0.0" />
                                <button className="max-btn" onClick={() => setStakeAmount(ghostBalance)}>MAX</button>
                            </div>
                            <div className="balance-info">Balance: {parseFloat(ghostBalance).toLocaleString()} GHOST</div>
                            <div className="input-group">
                                <label>Lock Tier</label>
                                <select value={stakeTier} onChange={(e) => setStakeTier(Number(e.target.value))}>
                                    {TIER_NAMES.map((name, i) => (
                                        <option key={i} value={i} disabled={stakeInfo && i < stakeInfo.lockTier}>{name} - {TIER_MULTIPLIERS[i]} rewards</option>
                                    ))}
                                </select>
                            </div>
                            <div className="modal-buttons">
                                <button onClick={() => setShowStakeModal(false)}>Cancel</button>
                                <button onClick={handleStake} disabled={loading}>{loading ? "Processing..." : "Stake"}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showUnstakeModal && (
                <div className="modal-overlay" onClick={() => setShowUnstakeModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Unstake GHOST</h2>
                        <div className="modal-content">
                            {!canUnstake ? (
                                <div className="lock-warning">
                                    <p>⚠️ Your tokens are still locked!</p>
                                    <p>Unlock date: {formatDate(stakeInfo?.lockEndTime)}</p>
                                    <p>{getTimeUntilUnlock()}</p>
                                </div>
                            ) : (
                                <>
                                    <div className="input-group">
                                        <label>Amount</label>
                                        <input type="number" value={unstakeAmount} onChange={(e) => setUnstakeAmount(e.target.value)} placeholder="0.0" />
                                        <button className="max-btn" onClick={() => setUnstakeAmount(stakeInfo?.amount || "0")}>MAX</button>
                                    </div>
                                    <div className="balance-info">Staked: {stakeInfo ? parseFloat(stakeInfo.amount).toLocaleString() : "0"} GHOST</div>
                                </>
                            )}
                            <div className="modal-buttons">
                                <button onClick={() => setShowUnstakeModal(false)}>Cancel</button>
                                <button onClick={handleUnstake} disabled={loading || !canUnstake}>{loading ? "Processing..." : (canUnstake ? "Unstake" : "Locked")}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDepositRewardsModal && (
                <div className="modal-overlay" onClick={() => setShowDepositRewardsModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>💰 Deposit Rewards</h2>
                        <div className="modal-content">
                            <p className="modal-info">Deposit GHOST tokens as rewards for stakers.</p>
                            {rewardStats && parseFloat(rewardStats.totalStaked) === 0 && (
                                <div className="lock-warning"><p>⚠️ Cannot deposit rewards yet - no stakers!</p></div>
                            )}
                            <div className="input-group">
                                <label>Amount to Deposit</label>
                                <input type="number" value={depositRewardsAmount} onChange={(e) => setDepositRewardsAmount(e.target.value)} placeholder="0.0" />
                                <button className="max-btn" onClick={() => setDepositRewardsAmount(ghostBalance)}>MAX</button>
                            </div>
                            <div className="balance-info">Your Balance: {parseFloat(ghostBalance).toLocaleString()} GHOST</div>
                            <div className="modal-buttons">
                                <button onClick={() => setShowDepositRewardsModal(false)}>Cancel</button>
                                <button onClick={handleDepositRewards} disabled={loading || (rewardStats && parseFloat(rewardStats.totalStaked) === 0)}>{loading ? "Processing..." : "Deposit Rewards"}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDelegateModal && (
                <div className="modal-overlay" onClick={() => setShowDelegateModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Delegate Voting Power</h2>
                        <div className="modal-content">
                            <p>Delegate your voting power to another address.</p>
                            <div className="input-group">
                                <label>Delegate Address</label>
                                <input type="text" value={delegateAddress} onChange={(e) => setDelegateAddress(e.target.value)} placeholder="0x..." />
                            </div>
                            <div className="modal-buttons">
                                <button onClick={() => setShowDelegateModal(false)}>Cancel</button>
                                <button onClick={handleDelegate} disabled={loading}>{loading ? "Processing..." : "Delegate"}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showProposalModal && (
                <div className="modal-overlay" onClick={() => setShowProposalModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Create Proposal</h2>
                        <div className="modal-content">
                            <p className="threshold-warning">Requires 1,000,000 GHOST staked to create a proposal</p>
                            <div className="input-group">
                                <label>Title</label>
                                <input type="text" value={proposalTitle} onChange={(e) => setProposalTitle(e.target.value)} placeholder="Proposal title..." />
                            </div>
                            <div className="input-group">
                                <label>Description</label>
                                <textarea value={proposalDescription} onChange={(e) => setProposalDescription(e.target.value)} placeholder="Describe your proposal..." rows={4} />
                            </div>
                            <div className="modal-buttons">
                                <button onClick={() => setShowProposalModal(false)}>Cancel</button>
                                <button onClick={handleCreateProposal} disabled={loading}>{loading ? "Processing..." : "Create Proposal"}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showVoteModal && selectedProposal && (
                <div className="modal-overlay" onClick={() => setShowVoteModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Vote on Proposal #{selectedProposal.id}</h2>
                        <div className="modal-content">
                            <h3>{selectedProposal.title}</h3>
                            <p>{selectedProposal.description}</p>
                            <div className="vote-info">Your voting power: {stakeInfo ? parseFloat(stakeInfo.amount).toLocaleString() : "0"} GHOST</div>
                            <div className="vote-buttons">
                                <button className="vote-for" onClick={() => handleVote(true)} disabled={loading}>✓ Vote For</button>
                                <button className="vote-against" onClick={() => handleVote(false)} disabled={loading}>✗ Vote Against</button>
                            </div>
                            <button className="cancel-btn" onClick={() => setShowVoteModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <footer className="voting-footer"><p>Ghost Protocol Governance • Powered by $GHOST</p></footer>
        </div>
    );
}

export default Voting;